import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { api } from "@/lib/api";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertOctagon, ShieldCheck, Search as SearchIcon, ExternalLink, ArrowLeft, MapPin, ShoppingBag } from "lucide-react";

export default function Brands() {
  const { user } = useAuth();
  const { loc } = useUserLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState("directory");    // directory | report | seller | mine
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [reports, setReports] = useState([]);
  const [myReports, setMyReports] = useState([]);
  const [mySellerApps, setMySellerApps] = useState([]);

  useEffect(() => { api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/counterfeit-reports").then(({ data }) => setReports(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!user) return;
    api.get("/counterfeit-reports/mine").then(({ data }) => setMyReports(data)).catch(() => {});
    api.get("/brands/authorised-sellers/mine").then(({ data }) => setMySellerApps(data)).catch(() => {});
  }, [user]);

  const filtered = useMemo(() => {
    if (!q) return brands;
    const s = q.toLowerCase();
    return brands.filter((b) => b.name.toLowerCase().includes(s));
  }, [q, brands]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="brands-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="brands-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <div>
          <h1 className="heading text-2xl font-extrabold flex items-center gap-2"><ShieldCheck className="text-[#E05E36]" /> Brand Authenticity</h1>
          <div className="text-sm text-stone-600">Report counterfeits · Register as an authorised distributor · Verify official brands.</div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-stone-200">
        {[
          ["directory", "Directory"],
          ["report", "Report Counterfeit"],
          ["seller", "Become Distributor"],
          ["mine", "My Reports"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-[#E05E36] text-[#E05E36]" : "border-transparent text-stone-500 hover:text-stone-800"}`} data-testid={`brands-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "directory" && (
        <>
          <div className="relative">
            <SearchIcon size={14} className="absolute left-3 top-3 text-stone-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brands…" className="pl-9" data-testid="brands-search" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((b) => <BrandCard key={b.brand_id} brand={b} onReport={() => { setTab("report"); }} />)}
          </div>
        </>
      )}

      {tab === "report" && (
        user
          ? <ReportForm brands={brands} defaultLat={loc?.lat} defaultLng={loc?.lng} onSubmitted={() => { setTab("mine"); api.get("/counterfeit-reports/mine").then(({ data }) => setMyReports(data)); }} />
          : <LoginPrompt />
      )}

      {tab === "seller" && (user ? <SellerApplyForm brands={brands} existing={mySellerApps} onSubmitted={() => api.get("/brands/authorised-sellers/mine").then(({ data }) => setMySellerApps(data))} /> : <LoginPrompt />)}

      {tab === "mine" && (
        <div className="space-y-3" data-testid="my-reports-list">
          <h3 className="text-sm font-bold uppercase tracking-widest text-stone-500">My counterfeit reports</h3>
          {(!user || myReports.length === 0) && <div className="text-sm text-stone-500 text-center py-8">No reports yet.</div>}
          {myReports.map((r) => (
            <Card key={r.report_id} className="border-stone-200">
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{r.product_name}</span>
                  <Badge className="text-xs" variant="outline">{r.status}</Badge>
                </div>
                <div className="text-xs text-stone-500">{r.brand_name} · {new Date(r.created_at).toLocaleString()}</div>
                {r.vendor_address && <div className="text-xs text-stone-600 flex items-center gap-1"><MapPin size={12} /> {r.vendor_address}</div>}
                <div className="text-sm text-stone-700 line-clamp-2">{r.description}</div>
              </CardContent>
            </Card>
          ))}

          <h3 className="text-sm font-bold uppercase tracking-widest text-stone-500 pt-4">My distributor applications</h3>
          {mySellerApps.length === 0 && <div className="text-sm text-stone-500 text-center py-6">No applications yet.</div>}
          {mySellerApps.map((s) => (
            <Card key={s.seller_id}><CardContent className="p-4 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{s.business_name}</span>
                <Badge variant="outline" className="text-xs">{s.status}</Badge>
              </div>
              <div className="text-xs text-stone-500">Brand: {s.brand_id} · Certificate #{s.certificate_number}</div>
              {s.coverage_area && <div className="text-xs text-stone-600">Coverage: {s.coverage_area}</div>}
            </CardContent></Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Recent public reports</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {reports.slice(0, 8).map((r, i) => (
            <div key={i} className="flex items-center justify-between border-b border-stone-100 py-1 text-sm" data-testid={`public-report-${i}`}>
              <span><b>{r.brand_name}</b> · {r.product_name}</span>
              <span className="text-xs text-stone-400">{r.vendor_address || "—"} · {r.status}</span>
            </div>
          ))}
          {reports.length === 0 && <div className="text-sm text-stone-500 text-center py-3">No public reports yet.</div>}
        </CardContent>
      </Card>
    </div>
  );
}


function BrandCard({ brand, onReport }) {
  return (
    <Card className="border-stone-200 hover:border-[#E05E36] transition-colors" data-testid={`brand-card-${brand.brand_id}`}>
      <CardContent className="p-4 flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-bold">{brand.name}</span>
            <Badge variant="outline" className="text-[10px]">{brand.country}</Badge>
          </div>
          <div className="text-xs text-stone-500">IP Authority: {brand.ip_authority}</div>
          {brand.official_url && (
            <a href={brand.official_url} target="_blank" rel="noreferrer" className="text-xs text-[#E05E36] hover:underline flex items-center gap-1 mt-1">
              <ExternalLink size={11} /> Official website
            </a>
          )}
          {brand.verify_endpoint && (
            <a href={brand.verify_endpoint} target="_blank" rel="noreferrer" className="text-xs text-[#E05E36] hover:underline flex items-center gap-1">
              <ShieldCheck size={11} /> Verify authenticity
            </a>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={onReport} className="rounded-full gap-1 shrink-0" data-testid={`brand-report-${brand.brand_id}`}>
          <AlertOctagon size={12} /> Report
        </Button>
      </CardContent>
    </Card>
  );
}


function ReportForm({ brands, defaultLat, defaultLng, onSubmitted }) {
  const [form, setForm] = useState({
    brand_id: "", product_name: "", seller_name: "",
    vendor_lat: defaultLat ?? "", vendor_lng: defaultLng ?? "",
    vendor_address: "", description: "", evidence_image_url: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.brand_id || !form.product_name || form.description.trim().length < 20) {
      return toast.error("Brand, product name, and a description of at least 20 chars are required");
    }
    setBusy(true);
    try {
      await api.post("/counterfeit-reports", {
        ...form,
        vendor_lat: form.vendor_lat ? parseFloat(form.vendor_lat) : null,
        vendor_lng: form.vendor_lng ? parseFloat(form.vendor_lng) : null,
      });
      toast.success("Report submitted — the brand has been notified.");
      onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setBusy(false); }
  };
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertOctagon size={16} /> Report a counterfeit product</CardTitle></CardHeader>
      <CardContent className="space-y-3" data-testid="report-form">
        <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
          <SelectTrigger data-testid="report-brand"><SelectValue placeholder="Choose brand" /></SelectTrigger>
          <SelectContent>{brands.map((b) => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Product name (as printed on the packaging)" data-testid="report-product" />
        <Input value={form.seller_name} onChange={(e) => setForm({ ...form, seller_name: e.target.value })} placeholder="Seller / shop name (optional)" data-testid="report-seller" />
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" step="0.000001" value={form.vendor_lat} onChange={(e) => setForm({ ...form, vendor_lat: e.target.value })} placeholder="Vendor lat" data-testid="report-lat" />
          <Input type="number" step="0.000001" value={form.vendor_lng} onChange={(e) => setForm({ ...form, vendor_lng: e.target.value })} placeholder="Vendor lng" data-testid="report-lng" />
        </div>
        <Input value={form.vendor_address} onChange={(e) => setForm({ ...form, vendor_address: e.target.value })} placeholder="Vendor address (optional)" data-testid="report-address" />
        <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What made you suspect it's counterfeit? (min 20 chars)" data-testid="report-desc" />
        <Button onClick={submit} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="report-submit">
          {busy ? "Submitting…" : "Submit report"}
        </Button>
      </CardContent>
    </Card>
  );
}


function SellerApplyForm({ brands, existing, onSubmitted }) {
  const [form, setForm] = useState({
    brand_id: "", business_name: "", distributor_certificate_url: "",
    certificate_number: "", valid_till: "", coverage_area: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.brand_id || !form.business_name || !form.distributor_certificate_url || !form.certificate_number) {
      return toast.error("All fields except coverage area are required");
    }
    setBusy(true);
    try {
      await api.post("/brands/authorised-sellers/apply", form);
      toast.success("Application submitted — awaiting brand verification.");
      onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShoppingBag size={16} /> Become an authorised distributor</CardTitle></CardHeader>
      <CardContent className="space-y-3" data-testid="seller-form">
        <div className="text-xs text-stone-600">Upload your brand's distributor certificate. Verified sellers get a green tick on their storefront and appear in official brand pages.</div>
        <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
          <SelectTrigger data-testid="seller-brand"><SelectValue placeholder="Choose brand" /></SelectTrigger>
          <SelectContent>{brands.map((b) => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Business name" data-testid="seller-name" />
        <Input value={form.certificate_number} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} placeholder="Certificate number" data-testid="seller-cert-no" />
        <Input type="url" value={form.distributor_certificate_url} onChange={(e) => setForm({ ...form, distributor_certificate_url: e.target.value })} placeholder="URL / data-URL of the certificate PDF or image" data-testid="seller-cert-url" />
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={form.valid_till} onChange={(e) => setForm({ ...form, valid_till: e.target.value })} placeholder="Valid till" data-testid="seller-valid" />
          <Input value={form.coverage_area} onChange={(e) => setForm({ ...form, coverage_area: e.target.value })} placeholder="Coverage area (Pan-India, MH…)" data-testid="seller-coverage" />
        </div>
        <Button onClick={submit} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="seller-submit">
          {busy ? "Submitting…" : "Submit application"}
        </Button>
        {existing.length > 0 && (
          <div className="pt-3 border-t border-stone-100">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">Your applications</div>
            {existing.map((s) => <div key={s.seller_id} className="text-sm">{s.business_name} · <span className="text-stone-500">{s.brand_id}</span> · <Badge variant="outline">{s.status}</Badge></div>)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function LoginPrompt() {
  return (
    <div className="text-center py-10">
      <div className="text-lg font-semibold mb-2">Please login to continue</div>
      <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="brands-login">Login with Google</Button>
    </div>
  );
}
